import type { ComponentProps } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { CopyAction, tokens } from '@hotjar/hotsauce';

// https://gitlab.com/hotjar/shared/frontend/design-system/-/blob/master/src/components/Actions/CopyAction.tsx#L22
const COPY_ANIMATION_DURATION = 3000;

interface Props {
  onCopy: ComponentProps<typeof CopyAction>['onCopy'];
  toCopy: ComponentProps<typeof CopyAction>['toCopy'];
  placement: ComponentProps<typeof CopyAction>['placement'];
}

const StyledCopyAction = styled(CopyAction)<{ hasCopied: boolean }>`
  margin-left: ${tokens.spacer.x1pt5};
  opacity: 0.7;

  &,
  :hover {
    color: ${tokens.color.text_default};
  }
  :hover {
    opacity: 1;
  }
  :focus {
    box-shadow: none;
  }

  ${({ hasCopied }) =>
    hasCopied &&
    css`
      &,
      :hover {
        color: ${tokens.color.mint_500};
        background-color: ${tokens.color.bg_hover};
      }
    `}
`;

export const CopyToClipboard = ({ onCopy, toCopy, placement }: Props): JSX.Element => {
  const [hasCopied, setHasCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!hasCopied) {
      return undefined;
    }

    const copyTimer = setTimeout(() => setHasCopied(false), COPY_ANIMATION_DURATION);
    return () => {
      clearTimeout(copyTimer);
    };
  }, [hasCopied]);

  return (
    <span data-testid="copy-to-clipboard-icon">
      <StyledCopyAction
        placement={placement}
        hasCopied={hasCopied}
        onCopy={() => {
          setHasCopied(true);

          if (onCopy) {
            onCopy();
          }
        }}
        toCopy={toCopy}
        description={t('common:player.url-bar.copy-url')}
      />
    </span>
  );
};
